'use client'
import { BLOCKS } from '@contentful/rich-text-types'

import { Accordion } from '@/components/common/Accordion/Accordion'
import { Heading } from '@/components/primitives/Heading/Heading'
import { cn } from '@/lib/utils'

import { CmsContainer } from '../CmsContainer/CmsContainer'
import RichTextEditor from '../RichTextEditor/RichTextEditor'

import type { TitleAndDescriptionWithAccordionsFields } from '@/types/contentful/titleAndDescriptionWithAccordionsType'
import type { Options } from '@contentful/rich-text-react-renderer'

const titleAlignClasses = {
  Center: 'text-center items-center justify-center',
  Left: 'text-left items-start justify-start',
  Right: 'text-right items-end justify-end',
}

const options: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => {
      const styledChildren = (children as (React.ReactNode | string)[])?.map((child, i) => {
        if (typeof child === 'string') {
          return child.split(/(→)/g).map((part, j) => {
            if (part === '→') {
              return (
                <span key={`${i}-${j}`} className="mr-1 text-lg">
                  {part}
                </span>
              )
            }

            return part
          })
        }

        return child
      })

      return <p className="pt-4 text-base first:pt-0">{styledChildren}</p>
    },
  },
}

export const TitleAndDescriptionWithAccordions = ({
  title,
  subtext: description,
  questions,
  topPadding,
  bottomPadding,
  titleAlign = 'Center',
  layout = 'Default',
}: TitleAndDescriptionWithAccordionsFields) => {
  const formatedTitle = title?.replace(/\s+/g, '-').toLowerCase()
  const isCompact = layout == 'Compact'
  const titleClass = titleAlign && titleAlignClasses[titleAlign]

  return (
    <section data-name="related-information" id={formatedTitle}>
      <CmsContainer
        topPadding={topPadding}
        bottomPadding={bottomPadding}
        className={cn('flex w-full scroll-mt-32 flex-col', isCompact ? `bg-white px-6 md:px-12` : 'items-center justify-center gap-8 bg-grey-100 px-4  lg:gap-16 lg:px-[120px]')}
      >
        <div className={cn('flex w-full max-w-xl flex-col', isCompact ? 'gap-2 md:gap-4' : 'gap-6', titleClass)}>
          {title && (
            <Heading as="h3" size="h1" className={cn(isCompact && 'text-left text-2xl md:!text-[40px]', titleClass)}>
              {title}
            </Heading>
          )}
          {description && <p className="text-center text-base text-grey-700">{description}</p>}
        </div>

        {!!questions?.length && (
          <div className={cn('w-full max-w-[788px]', !isCompact && 'rounded-lg bg-white px-8 shadow-md')}>
            {
              <Accordion
                triggerClasses={'text-left py-5 md:py-6'}
                data={questions.map(({ fields: { answer, title } }) => {
                  return {
                    title,
                    value: title ?? '',
                    content: <div className="flex flex-col gap-4 text-grey-500">{!!answer && <RichTextEditor styleOptions={options} content={answer} />}</div>,
                  }
                })}
              />
            }
          </div>
        )}
      </CmsContainer>
    </section>
  )
}
